// /src/EstimateList.js
import React, { useEffect, useState } from "react";
import api from "./services/api";
import generateEstimatePDF from "./EstimatePDF";
import debounce from "lodash.debounce";
import { Link, useNavigate } from "react-router-dom";

const EstimateList = () => {
  const [estimates, setEstimates] = useState([]);
  const [selectedEstimate, setSelectedEstimate] = useState(null);
  const navigate = useNavigate();
  const [loadingAll, setLoadingAll] = useState(true);
  const [loadingView, setLoadingView] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");

  const limit = 10;

  useEffect(() => {
    fetchPaginatedData(page, search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search]);

  const fetchPaginatedData = (currentPage, searchTerm = "") => {
    setLoadingAll(true);
    api
      .get(`/estimates/paginated`, {
        params: { page: currentPage, limit, search: searchTerm },
      })
      .then((res) => {
        setEstimates(res.data.data || []);
        setTotalPages(res.data.totalPages || 1);
      })
      .catch((err) => {
        console.error(err);
        setEstimates([]);
      })
      .finally(() => setLoadingAll(false));
  };

  const handleView = (id) => {
    setLoadingView(true);
    api
      .get(`/estimates/${id}`)
      .then((res) => setSelectedEstimate(res.data))
      .catch((err) => console.error(err))
      .finally(() => setLoadingView(false));
  };

  const handleSearchChange = debounce((value) => {
    setPage(1);
    setSearch(value);
  }, 400);

  const rupee = (val) =>
    typeof val === "number"
      ? val.toLocaleString("en-IN", { maximumFractionDigits: 0 })
      : val;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-indigo-950 text-white overflow-x-hidden pb-20">
      {/* Animated Background Orbs */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <div className="orb absolute w-[500px] h-[500px] -top-20 -left-20 bg-purple-600/20 rounded-full blur-[100px]"></div>
        <div className="orb absolute w-[400px] h-[400px] bottom-0 right-0 bg-indigo-600/20 rounded-full blur-[100px]" style={{ animationDelay: "-5s" }}></div>
        <div className="orb absolute w-[300px] h-[300px] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-amber-600/10 rounded-full blur-[100px]" style={{ animationDelay: "-7s" }}></div>
      </div>

      <div className="container mx-auto px-4 md:px-8 py-8 relative z-10 max-w-7xl">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-4xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-amber-200 via-orange-200 to-purple-200 inline-block">
            Repair Estimates
          </h1>
          <p className="text-purple-300/60 mt-2 text-sm uppercase tracking-widest font-medium">View and manage repair service estimates</p>
        </div>

        {/* Search + Actions Bar */}
        <div className="glass-panel p-6 mb-8 rounded-3xl border border-white/10 shadow-2xl backdrop-blur-xl bg-white/5 relative overflow-hidden">
           <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/10 blur-[80px] rounded-full pointer-events-none"></div>
           
           <div className="relative z-10 flex flex-col md:flex-row gap-6 items-center justify-between">
            <div className="relative flex-1 w-full max-w-2xl">
              <i className="fas fa-search absolute left-4 top-1/2 -translate-y-1/2 text-purple-300/50 text-lg"></i>
              <input
                type="text"
                placeholder="Search by customer, job card, or mobile..."
                className="w-full pl-12 pr-4 py-4 rounded-xl bg-slate-900/50 border border-white/10 focus:border-amber-500/50 focus:outline-none focus:ring-2 focus:ring-amber-500/20 text-white placeholder-white/20 transition-all font-medium"
                onChange={(e) => handleSearchChange(e.target.value)}
              />
            </div>
            
            <Link to="/estimate" className="group relative px-8 py-4 rounded-xl font-bold text-white shadow-lg overflow-hidden transition-all hover:scale-[1.02] w-full md:w-auto text-center">
                 <div className="absolute inset-0 bg-gradient-to-r from-amber-600 to-orange-600 transition-all group-hover:opacity-90"></div>
                 <div className="relative flex items-center justify-center gap-2">
                    <i className="fas fa-plus"></i> New Estimate
                 </div>
            </Link>
          </div>
        </div>

        {/* Desktop Table View */}
        <div className="hidden md:block glass-panel rounded-3xl overflow-hidden border border-white/10 shadow-xl bg-slate-900/30 backdrop-blur-md">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-white/5 text-purple-200 text-xs uppercase tracking-wider font-bold border-b border-white/10">
                  <th className="px-6 py-5 text-left">Ref Number</th>
                  <th className="px-6 py-5 text-left">Customer</th>
                  <th className="px-6 py-5 text-left">City</th>
                  <th className="px-6 py-5 text-left">Date</th>
                  <th className="px-6 py-5 text-left">Amount</th>
                  <th className="px-6 py-5 text-center">Action</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-white/5">
                {loadingAll ? (
                  <tr>
                    <td colSpan={6} className="p-12 text-center">
                      <div className="flex flex-col items-center gap-3">
                         <div className="w-8 h-8 border-2 border-amber-500/30 border-t-amber-400 rounded-full animate-spin"></div>
                         <span className="text-purple-300/50 text-sm">Loading estimates...</span>
                      </div>
                    </td>
                  </tr>
                ) : estimates.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="p-12 text-center text-white/30 italic">
                      No estimates found matching your search.
                    </td>
                  </tr>
                ) : (
                  estimates.map((est, idx) => (
                    <tr key={est.id || idx} className="group hover:bg-white/5 transition-colors">
                      <td className="px-6 py-5 font-mono text-purple-300 font-medium">
                         <span className="bg-amber-500/10 text-amber-300 px-2 py-1 rounded-md text-sm border border-amber-500/20">
                            {est.estimate_no || est.job_card_no || `#${est.id}`}
                         </span>
                      </td>
                      <td className="px-6 py-5 font-medium text-white">
                          <div className="flex flex-col">
                              <span>{est.customer_name}</span>
                              <span className="text-xs text-white/30 font-normal">{est.customer_mobile}</span>
                          </div>
                      </td>
                      <td className="px-6 py-5 text-purple-200/70">{est.customer_city}</td>
                      <td className="px-6 py-5 text-sm text-white/50 font-mono">
                          {est.estimate_date ? new Date(est.estimate_date).toLocaleDateString('en-GB') : "-"}
                      </td>
                      <td className="px-6 py-5 font-bold text-lg text-emerald-400 font-mono">
                          ₹{rupee(Number(est.total_amount) || 0)}
                      </td>
                      <td className="px-6 py-5 text-center">
                        <button
                          onClick={() => handleView(est.id)}
                          className="w-10 h-10 rounded-full bg-white/5 hover:bg-white/10 text-amber-300 hover:text-white transition-all flex items-center justify-center mx-auto border border-white/5"
                          title="View Details"
                        >
                          <i className="fas fa-eye text-sm"></i>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Mobile Card View */}
         <div className="md:hidden space-y-4">
          {loadingAll ? (
             <div className="p-12 text-center">
                <div className="w-8 h-8 border-2 border-amber-500/30 border-t-amber-400 rounded-full animate-spin mx-auto mb-3"></div>
                <span className="text-purple-300/50 text-sm">Loading estimates...</span>
             </div>
          ) : estimates.length === 0 ? (
            <div className="text-center text-white/30 italic p-8 glass-panel rounded-2xl bg-white/5">
              No estimates found.
            </div>
          ) : (
            estimates.map((est, idx) => (
              <div key={est.id || idx} className="glass-panel rounded-2xl p-5 border border-white/10 bg-slate-900/40 relative overflow-hidden group">
                <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-amber-500 to-orange-500 opacity-50 group-hover:opacity-100 transition-opacity"></div>
                
                <div className="flex justify-between items-start mb-3 pl-3">
                  <div>
                    <h3 className="font-bold text-white text-lg">{est.customer_name}</h3>
                    <div className="text-white/40 text-sm flex items-center gap-2">
                       <i className="fas fa-map-marker-alt text-xs"></i> {est.customer_city}
                    </div>
                  </div>
                  <span className="bg-amber-500/10 text-amber-300 px-2 py-1 rounded text-xs border border-amber-500/20 font-mono">
                    {est.estimate_no || est.job_card_no}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4 pl-3">
                  <div>
                    <p className="text-[10px] text-purple-300/50 uppercase tracking-wider font-bold">Date</p>
                    <p className="text-sm text-white/80 font-mono">{est.estimate_date ? new Date(est.estimate_date).toLocaleDateString('en-GB') : "-"}</p>
                  </div>
                  <div className="text-right">
                     <p className="text-[10px] text-purple-300/50 uppercase tracking-wider font-bold">Amount</p>
                     <p className="text-lg font-bold text-emerald-400 font-mono">₹{rupee(Number(est.total_amount) || 0)}</p>
                  </div>
                </div>

                <button
                  onClick={() => handleView(est.id)}
                  className="w-full bg-white/5 hover:bg-white/10 border border-white/10 text-white/80 py-3 rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2"
                >
                  View Details <i className="fas fa-arrow-right text-xs opacity-50"></i>
                </button>
              </div>
            ))
          )}
        </div>

        {/* Pagination */}
        <div className="mt-8 flex flex-col md:flex-row items-center justify-between gap-4">
             <button
               onClick={() => setPage(Math.max(1, page - 1))}
               disabled={page === 1}
               className={`flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-sm transition-all ${
                   page === 1 
                   ? "text-white/20 cursor-not-allowed" 
                   : "text-purple-300 hover:bg-white/5 hover:text-white"
               }`}
             >
               <i className="fas fa-chevron-left"></i> Previous
             </button>

             <div className="flex gap-2">
               {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                   let p = i + 1;
                   // Simple logic to show current page range (can be improved)
                   if (totalPages > 5 && page > 3) p = page - 2 + i;
                   if (p > totalPages) return null;

                   return (
                       <button
                           key={p}
                           onClick={() => setPage(p)}
                           className={`w-10 h-10 rounded-xl font-bold text-sm flex items-center justify-center transition-all ${
                               page === p
                               ? "bg-amber-600 text-white shadow-lg shadow-amber-900/50"
                               : "bg-white/5 text-purple-200 hover:bg-white/10"
                           }`}
                       >
                           {p}
                       </button>
                   );
               })}
             </div>

             <button
               onClick={() => setPage(Math.min(totalPages, page + 1))}
               disabled={page === totalPages}
               className={`flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-sm transition-all ${
                   page === totalPages 
                   ? "text-white/20 cursor-not-allowed" 
                   : "text-purple-300 hover:bg-white/5 hover:text-white"
               }`}
             >
               Next <i className="fas fa-chevron-right"></i>
             </button>
        </div>

      </div>

      {/* View Estimate Modal */}
      {(loadingView || selectedEstimate) && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-[fadeIn_0.2s_ease-out]">
          <div className="relative w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-3xl bg-slate-900 border border-white/10 shadow-2xl flex flex-col">
            
             {/* Modal Close */}
             <button
               onClick={() => setSelectedEstimate(null)}
               className="absolute top-4 right-4 z-40 w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 transition-all"
            >
               <i className="fas fa-times"></i>
            </button>

            {loadingView ? (
                 <div className="flex flex-col items-center justify-center h-64">
                    <div className="w-10 h-10 border-4 border-amber-500/30 border-t-amber-400 rounded-full animate-spin mb-4"></div>
                    <p className="text-purple-300/50 text-sm">Loading details...</p>
                 </div>
            ) : (
                <>
                   {/* Modal Header */}
                   <div className="p-8 border-b border-white/5 bg-white/[0.02]">
                       <div className="flex items-center gap-3 mb-2">
                           <span className="px-3 py-1 rounded-full bg-amber-500/20 text-amber-300 text-xs font-bold border border-amber-500/20 uppercase tracking-wider">
                               Estimate
                           </span>
                           <span className="text-white/30 text-sm font-mono">{selectedEstimate.estimate_date ? new Date(selectedEstimate.estimate_date).toLocaleDateString('en-GB') : ""}</span>
                       </div>
                       <h2 className="text-3xl font-bold text-white mb-1">
                          {selectedEstimate.customer_name}
                       </h2>
                       <p className="text-white/50 text-lg">{selectedEstimate.estimate_no || selectedEstimate.job_card_no}</p>
                   </div>

                   {/* Modal Content */}
                   <div className="flex-1 overflow-y-auto p-8 custom-scrollbar">
                       
                       {/* Info Grid */}
                       <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                           <div className="space-y-4">
                               <h3 className="text-purple-300 font-bold uppercase text-xs tracking-wider border-b border-white/10 pb-2">Customer Details</h3>
                               <div className="grid grid-cols-2 gap-4">
                                   <div>
                                       <p className="text-white/30 text-xs">Mobile</p>
                                       <p className="text-white">{selectedEstimate.customer_mobile}</p>
                                   </div>
                                   <div>
                                       <p className="text-white/30 text-xs">City</p>
                                       <p className="text-white">{selectedEstimate.customer_city}</p>
                                   </div>
                               </div>
                           </div>
                           <div className="space-y-4">
                               <h3 className="text-amber-300 font-bold uppercase text-xs tracking-wider border-b border-white/10 pb-2">Job Details</h3>
                               <div className="grid grid-cols-2 gap-4">
                                   <div>
                                       <p className="text-white/30 text-xs">Job Card</p>
                                       <p className="text-white font-mono">{selectedEstimate.job_card_no || "N/A"}</p>
                                   </div>
                                   <div>
                                       <p className="text-white/30 text-xs">Motor Info</p>
                                       <p className="text-white">{selectedEstimate.motor_hp} / {selectedEstimate.motor_type}</p>
                                   </div>
                               </div>
                           </div>
                       </div>

                       <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                           <i className="fas fa-tools text-amber-400"></i> Parts & Service
                       </h3>
                       
                       <div className="bg-white/5 rounded-2xl overflow-hidden border border-white/5 mb-8">
                           <table className="w-full text-sm text-left">
                               <thead className="text-xs text-purple-200 uppercase bg-white/5 font-bold">
                                   <tr>
                                       <th className="px-6 py-4">Part / Service</th>
                                       <th className="px-6 py-4 text-right">Price</th>
                                       <th className="px-6 py-4 text-center">Qty</th>
                                       <th className="px-6 py-4 text-right">Total</th>
                                   </tr>
                               </thead>
                               <tbody className="divide-y divide-white/5">
                                  {selectedEstimate.items && selectedEstimate.items.map((item, idx) => (
                                      <tr key={idx} className="hover:bg-white/[0.02]">
                                          <td className="px-6 py-4 font-medium text-white">
                                              {item.name}
                                              {item.description && <div className="text-xs text-white/50 mt-1">{item.description}</div>}
                                          </td>
                                          <td className="px-6 py-4 text-right text-white/70">
                                              ₹{rupee(
                                                  (selectedEstimate.include_gst !== 0 && selectedEstimate.include_gst !== false)
                                                      ? (Number(item.price) || 0) / 1.18
                                                      : (Number(item.price) || 0)
                                              )}
                                          </td>
                                          <td className="px-6 py-4 text-center text-white/70">{item.quantity}</td>
                                          <td className="px-6 py-4 text-right text-amber-200 font-bold">
                                              ₹{rupee(
                                                  ((selectedEstimate.include_gst !== 0 && selectedEstimate.include_gst !== false)
                                                      ? (Number(item.price) || 0) / 1.18
                                                      : (Number(item.price) || 0)) * (Number(item.quantity) || 0)
                                              )}
                                          </td>
                                      </tr>
                                  ))}
                               </tbody>
                           </table>
                       </div>

                       {/* Notes Section */}
                       {selectedEstimate.description && (
                         <div className="mb-8 bg-white/5 p-4 rounded-2xl border border-white/5">
                             <p className="text-xs text-white/30 uppercase tracking-wider font-bold mb-2">Technician Notes</p>
                             <p className="text-white/80 whitespace-pre-wrap text-sm leading-relaxed">{selectedEstimate.description}</p>
                         </div>
                       )}

                       {/* Summary & Totals */}
                       <div className="flex flex-col items-end gap-2 border-t border-white/10 pt-6 mb-8">
                           { Number(selectedEstimate.discount_amount) > 0 && (
                               <div className="flex items-center gap-8 text-sm mb-2">
                                   <span className="text-white/50">Discount</span>
                                   <span className="text-red-400 font-bold font-mono">
                                       - ₹{rupee(Number(selectedEstimate.discount_amount || 0))}
                                   </span>
                               </div>
                           )}
                           { (selectedEstimate.include_gst !== 0 && selectedEstimate.include_gst !== false) ? (
                               <React.Fragment>
                                   <div className="flex items-center gap-8 text-sm">
                                       <span className="text-white/50">Taxable Amount</span>
                                       <span className="text-white font-bold font-mono">₹{rupee((Number(selectedEstimate.total_amount) || 0) / 1.18)}</span>
                                   </div>
                                   <div className="flex items-center gap-8 text-sm">
                                       <span className="text-white/50">CGST (9%)</span>
                                       <span className="text-white font-bold font-mono">₹{rupee(((Number(selectedEstimate.total_amount) || 0) - ((Number(selectedEstimate.total_amount) || 0) / 1.18)) / 2)}</span>
                                   </div>
                                   <div className="flex items-center gap-8 text-sm">
                                       <span className="text-white/50">SGST (9%)</span>
                                       <span className="text-white font-bold font-mono">₹{rupee(((Number(selectedEstimate.total_amount) || 0) - ((Number(selectedEstimate.total_amount) || 0) / 1.18)) / 2)}</span>
                                   </div>
                               </React.Fragment>
                           ) : (
                               <div className="flex items-center gap-8 text-sm">
                                   <span className="text-white/50">Total Amount</span>
                                   <span className="text-white font-bold font-mono">₹{rupee(Number(selectedEstimate.total_amount) || 0)}</span>
                               </div>
                           )}
                           <div className="w-full h-px bg-white/10 my-2 max-w-xs"></div>
                           <div className="flex items-center gap-8 text-xl">
                               <span className="text-emerald-400 font-bold">Net Total</span>
                               <span className="text-emerald-400 font-black font-mono">₹{rupee(Number(selectedEstimate.total_amount))}</span>
                           </div>
                       </div>
                   </div>

                   {/* Modal Actions Footer */}
                   <div className="p-6 border-t border-white/10 bg-white/5 flex flex-wrap items-center justify-end gap-3">
                       
                        <button
                            onClick={() => navigate(`/edit-estimate/${selectedEstimate.id}`)}
                            className="px-5 py-3 rounded-xl font-bold bg-white/5 border border-white/10 text-white hover:bg-white/10 transition-all flex items-center gap-2"
                        >
                            <i className="fas fa-edit text-amber-400"></i> Edit
                        </button>

                         <button
                            onClick={() => {
                                const phone = (selectedEstimate.customer_mobile || "").replace(/\D/g, "");
                                const estNo = selectedEstimate.estimate_no || selectedEstimate.job_card_no || "";
                                const message = encodeURIComponent(
                                    `Hello ${selectedEstimate.customer_name}, here is your repair estimate #${estNo}.`
                                );
                                window.open(`https://wa.me/91${phone}?text=${message}`, "_blank");
                            }}
                            className="px-5 py-3 rounded-xl font-bold bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 hover:bg-emerald-500/20 transition-all flex items-center gap-2"
                        >
                            <i className="fab fa-whatsapp"></i> WhatsApp
                        </button>

                        <button
                            onClick={() => {
                                const pdfData = {
                                    customer: {
                                        name: selectedEstimate.customer_name,
                                        city: selectedEstimate.customer_city,
                                        mobile: selectedEstimate.customer_mobile,
                                        date: selectedEstimate.estimate_date.split('T')[0]
                                    },
                                    jobDetails: {
                                        jobCardNo: selectedEstimate.job_card_no,
                                        hp: selectedEstimate.motor_hp,
                                        type: selectedEstimate.motor_type
                                    },
                                    items: selectedEstimate.items,
                                    total: parseFloat(selectedEstimate.total_amount) + parseFloat(selectedEstimate.discount_amount || 0),
                                    discountAmount: parseFloat(selectedEstimate.discount_amount || 0),
                                    description: selectedEstimate.description,
                                    estimateNo: selectedEstimate.estimate_no,
                                    includeGst: selectedEstimate.include_gst !== 0 && selectedEstimate.include_gst !== false
                                };
                                generateEstimatePDF(pdfData, "print");
                            }}
                            className="px-5 py-3 rounded-xl font-bold bg-amber-600 hover:bg-amber-500 text-white shadow-lg shadow-amber-900/20 transition-all flex items-center gap-2"
                        >
                            <i className="fas fa-print"></i> Print
                        </button>

                        <button
                            onClick={() => {
                                const pdfData = {
                                    customer: {
                                        name: selectedEstimate.customer_name,
                                        city: selectedEstimate.customer_city,
                                        mobile: selectedEstimate.customer_mobile,
                                        date: selectedEstimate.estimate_date.split('T')[0]
                                    },
                                    jobDetails: {
                                        jobCardNo: selectedEstimate.job_card_no,
                                        hp: selectedEstimate.motor_hp,
                                        type: selectedEstimate.motor_type
                                    },
                                    items: selectedEstimate.items,
                                    total: parseFloat(selectedEstimate.total_amount) + parseFloat(selectedEstimate.discount_amount || 0),
                                    discountAmount: parseFloat(selectedEstimate.discount_amount || 0),
                                    description: selectedEstimate.description,
                                    estimateNo: selectedEstimate.estimate_no,
                                    includeGst: selectedEstimate.include_gst !== 0 && selectedEstimate.include_gst !== false
                                };
                                generateEstimatePDF(pdfData, "download");
                            }}
                            className="px-5 py-3 rounded-xl font-bold bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-900/20 transition-all flex items-center gap-2"
                        >
                            <i className="fas fa-file-pdf"></i> Download PDF
                        </button>

                   </div>
                </>
            )}
          </div>
        </div>
      )}

      {/* Styles */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        .orb {
          animation: float 10s ease-in-out infinite;
        }
        @keyframes fadeIn {
            from { opacity: 0; transform: scale(0.95); }
            to { opacity: 1; transform: scale(1); }
        }
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.05);
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.2);
        }
        .glass-panel {
            background: rgba(255, 255, 255, 0.03);
            backdrop-filter: blur(16px);
        }
      `}</style>
    </div>
  );
};

export default EstimateList;
